import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { useValueWithUnitColumnType } from 'components/Blotters/Trades/Grid/connect/useValueWithUnitColumn';
import { getStatusFieldColor } from 'components/shared/Blotter/BlotterPanel/Fields/StatusField/StatusField';
import { StatusToColorMapper } from 'components/shared/Blotter/Grid/CellRenderers/StatusCellRenderer';
import { useContextMenuItems } from 'components/shared/Blotter/Grid/Hooks/useContextMenuItems';
import { useDefaultColumnDefinition } from 'components/shared/Blotter/Grid/Hooks/useDefaultColumnDefinition';
import { createMasterDetailTechnicalColumn } from 'components/shared/Blotter/Grid/Hooks/useMasterDetailTechnicalColumn';
import {
  useServerSideDefaultColumnTypes,
  useWithExplicitFilering,
} from 'components/shared/Blotter/Grid/Hooks/useServerSideDefaultColumnTypes';
import { useServerSideStatusColumnType } from 'components/shared/Blotter/Grid/Hooks/useStatusColumnType';
import { useTradeAuthorizationStatusColumnType } from 'components/shared/Blotter/Grid/Hooks/useTradeAuthorizationStatusColumnType';
import { getHeaderForBlotterIntlKey } from 'components/shared/Blotter/utils';
import { useCallback, useMemo, useState } from 'react';
import { DlpProductBlotterItem } from 'state/blotters/trades/types/blotterItem';

type DlpProductStatus = 'active' | 'inactive';

export const getProductStatusFieldColor: StatusToColorMapper<DlpProductStatus> = status => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'secondary';
    default:
      return undefined;
  }
};

const getHeaderIntlKey = getHeaderForBlotterIntlKey('dlpProducts');

const defaultColumn: Partial<ColDef<DlpProductBlotterItem>> = {
  floatingFilter: false,
  enableCellChangeFlash: true,
};
const stringWithMasterDetail = createMasterDetailTechnicalColumn();
export function useDlpTradesBlottersProps() {
  const defaultColumnDef = useDefaultColumnDefinition<any>(getHeaderIntlKey, defaultColumn);

  const statusColumn = useServerSideStatusColumnType(getStatusFieldColor);
  const tradeAuthorizationColumn = useTradeAuthorizationStatusColumnType();
  const productStatus = useServerSideStatusColumnType(getProductStatusFieldColor);
  const valueWithUnitColumnType = useValueWithUnitColumnType();

  const customCols = useMemo(
    () => ({
      status: statusColumn,
      productStatus: productStatus,
      valueWithUnit: valueWithUnitColumnType,
      tradeAuthorizationStatus: tradeAuthorizationColumn,
      stringWithMasterDetail,
    }),
    [statusColumn, productStatus, tradeAuthorizationColumn, valueWithUnitColumnType],
  );
  const columnTypesWithFilteringEverywhere = useServerSideDefaultColumnTypes<any>(customCols);

  const columnTypesWithAppropriateFiltering = useWithExplicitFilering(columnTypesWithFilteringEverywhere, undefined);
  const [gridApi, setGridApi] = useState<GridApi>();

  const gridReady = useCallback((event: GridReadyEvent) => {
    setGridApi(event.api);
  }, []);

  const getContextMenuItems = useContextMenuItems('Blotter My Trades', gridApi);
  return {
    defaultColumnDef,
    columnTypes: columnTypesWithAppropriateFiltering,
    gridReady,
    getContextMenuItems,
    gridApi,
  };
}
